import React, { useEffect } from 'react';
import axios, { API } from 'config/axios';
import { withTranslation, Router, i18n } from 'config/i18n';
import getLocalizedUrl from 'utils/getLocalizedUrl';

// Material UI Core
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  CardMedia,
  CardContent,
  Card,
  Box,
  Container,
  Hidden,
  Grid,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

// React components
import { useLayoutSnackbar, useLayoutState } from 'components/shared/Layout';
import LightTextField from 'components/shared/LightTextField';
import Link from 'components/shared/Link';
// Material UI icons
import HelpIcon from '@material-ui/icons/Help';
import useHandleResponse from 'utils/useHandleResponse';

const useStyles = makeStyles((theme) => ({
  root: { display: 'flex' },

  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    marginTop: theme.spacing(3),
  },
  cover: {
    width: '30%',
    backgroundPosition: 'top center',
  },
  mainTitle: {
    marginBottom: theme.spacing(4),
  },
  header: {
    paddingLeft: theme.spacing(0),
    margin: theme.spacing(4, 0),
    [theme.breakpoints.down('sm')]: {
      borderLeft: 0,
      paddingLeft: theme.spacing(0),
      margin: theme.spacing(2, 0),
    },
  },
  label: {
    alignItems: 'start',
  },
  labelHead: {
    marginTop: theme.spacing(1),
    fontSize: theme.typography.pxToRem(16),
  },
  optionsLabel: {
    color: theme.palette.grey[800],
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  labelSubHead: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.5,
    color: theme.palette.grey[300],
  },
  form: {
    paddingBottom: theme.spacing(2),
    "& > [class*='MuiGrid-container']": {
      marginTop: theme.spacing(-1),
      marginBottom: theme.spacing(-1),
    },
  },
  lookup: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  divider: {
    position: 'relative',
    margin: theme.spacing(2.2, 0),
    '& span': {
      position: 'absolute',
      padding: '0 10px',
      top: '50%',
      textTransform: 'uppercase',
      backgroundColor: theme.palette.common.white,
      color: theme.palette.grey[200],
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
  },
  footerWrapper: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    justifyContent: 'flex-start',
  },
  infoNote: {
    background: theme.palette.grey[50],
    color: theme.palette.grey[500],
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& svg': {
      color: theme.palette.grey[500],
    },
  },
  smallNote: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.grey[300],
  },
  footer: {
    display: 'flex',
    "& [class*='MuiTypography-root']": {
      marginLeft: theme.typography.pxToRem(3),
      lineHeight: 1.8,
    },
    '& a': {
      color: theme.palette.common.black,
      fontWeight: 700,
      textDecoration: 'underline',
      textTransform: 'uppercase',
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
  },
}));
const CheckinPage = (props) => {
  const classes = useStyles();
  const { layoutState } = useLayoutState();

  const currentUser = layoutState.current_user;

  const { handleError } = useHandleResponse();

  const {
    t,
  } = props;

  const [snackbar, setSnackbar] = useLayoutSnackbar();
  const initialState = {
    eagleShare: {
      confirmation_number: '',
      email: currentUser && !currentUser.privileged ? currentUser.email : '',
    },
  };
  const [eagleShareForm, setEagleShareForm] = React.useState(initialState.eagleShare); // 2nd form

  useEffect(() => {
    initialState.eagleShare.email = currentUser && !currentUser.privileged ? currentUser.email : '';
    setEagleShareForm({
      ...eagleShareForm,
      email: initialState.eagleShare.email,
    });
  }, [currentUser]);
  const [errorMsgs, setErrorMsgs] = React.useState({
    form1: {},
    form2: {},
    eagleShare: {},
  });

  const handleEagleShareForm = (e) => {
    setEagleShareForm({
      ...eagleShareForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsgs({
      form1: {},
      form2: {},
      eagleShare: {},
    });
    const submitedData = { ...eagleShareForm };

    axios
      .post(API.vrs.lookup, {
        authenticity_token: layoutState.authenticity_token,
        lookup: submitedData,
        eagleshare_reservation: true,
      })
      .then((res) => {
        if (res.data.redirection_url) {
          Router.push(res.data.redirection_url);
        }
      })
      .catch((error) => {
        try {
          switch (error.response.status) {
            case 404:
            case 406:
              setSnackbar({
                ...snackbar,
                open: true,
                severity: 'error',
                message: error.response.data.message,
              });
              break;
            default:
              handleError(error);
          }
        } catch {
          handleError(error);
        }
      });
  };
  const esForm = (
    <form onSubmit={(e) => handleSubmit(e, true)} className={classes.form}>
      <Grid container spacing={4}>
        <Grid item md={6} sm={12}>
          <LightTextField
            required
            error={Boolean(errorMsgs.confirmation_number)}
            fullWidth
            color="secondary"
            label={t('check_in:booking_confirmation_number')}
            variant="outlined"
            name="confirmation_number"
            value={eagleShareForm.confirmation_number}
            onChange={handleEagleShareForm}
            helperText={errorMsgs.confirmation_number}
            inputProps={{ 'data-testid': 'es-checkin-lookup-confirmation' }}
          />
        </Grid>
        <Grid item md={6} sm={12}>
          <LightTextField
            required
            error={Boolean(errorMsgs.email)}
            fullWidth
            color="secondary"
            label={t('fe_check_in:primary_rider_email')}
            variant="outlined"
            name="email"
            value={eagleShareForm.email}
            onChange={handleEagleShareForm}
            helperText={errorMsgs.email}
            disabled={initialState.eagleShare.email !== ''}
            inputProps={{ 'data-testid': 'es-checkin-lookup-email' }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={4} justify="flex-end">
        <Grid item md={3} xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            className={classes.lookup}
            data-testid="es-checkin-lookup-submit"
          >
            {t('check_in:look_up')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );

  return (
    <Box>
      <Card className={classes.root}>
        <Hidden mdDown>
          <CardMedia
            className={classes.cover}
            image="/imgs/side_banner.jpg"
            title="EagleRider Motorcycles"
          />
        </Hidden>

        <CardContent className={classes.content}>
          <Container>
            <Typography variant="h3" className={classes.mainTitle}>
              {t('fe_check_in:find_your_booking')}
            </Typography>
            <Alert severity="info" className={classes.infoNote}>
              {t('fe_check_in:checkin_required')}
              {' '}
              <span className={classes.smallNote}>{t('fe_check_in:checkin_failure')}</span>
            </Alert>
            {esForm}

            <div className={classes.footerWrapper}>
              <div className={classes.footer}>
                <HelpIcon />
                <Typography variant="body2">
                  {t('check_in:q_about_rental')}
                  <Link
                    href={getLocalizedUrl('/faqs/eagleshare-renter', i18n.language)}
                    color="primary"
                  >
                    &nbsp;
                    {t('check_in:see_our_faqs')}
                  </Link>
                </Typography>
              </div>
            </div>
          </Container>
        </CardContent>
      </Card>
    </Box>
  );
};
CheckinPage.getInitialProps = async () => ({
  er: true,
  namespacesRequired: ['check_in', 'fe_check_in'],
});
CheckinPage.propTypes = {
};

export default withTranslation(['check_in', 'fe_check_in'])(CheckinPage);
